import React from 'react';
import clsx from 'clsx';
import { useDrop } from 'react-dnd';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey, lightBlue, red } from '@mui/material/colors';

import { BlockOutlined } from '@mui/icons-material';

import { BoardCard } from './BoardCard';
import { BoardColumnProps } from './types';
import { DND_CARD_TYPE } from './constants';

export function BoardColumn<T>({
  id,
  title,
  cards,
  metaData,
  renderCard,
  onMoveCard,
  renderColumnHeader,
  canDropColumn,
}: BoardColumnProps<T>) {
  const classes = useStyles();

  const [{ isOver, canDrop }, dropCard] = useDrop({
    accept: DND_CARD_TYPE,
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    canDrop: (dragItem: any) => {
      if (!metaData.isSupportDAD || dragItem.columnId === id) return false;
      if (canDropColumn) return canDropColumn(dragItem.columnId, id);
      return true;
    },
    drop: (dragItem: any) => {
      if (dragItem.columnId !== id && metaData.isSupportDAD) {
        if (canDropColumn && !canDropColumn(dragItem.columnId, id)) return;
        onMoveCard({
          toColumnId: id,
          toIndex: 0,
          itemIndex: dragItem.index,
          fromColumnId: dragItem.columnId,
        });
      }
    },
  });

  return (
    <Box className={classes.boardColumn}>
      <Box className="headerSpace">
        {renderColumnHeader ? (
          renderColumnHeader({ id, title, cards, metaData })
        ) : (
          <Typography sx={{ color: metaData.nameColor }} variant="h6">
            {title}
          </Typography>
        )}
      </Box>

      <Box
        ref={dropCard}
        className={clsx('cardsSpace', { cardsOverSpace: isOver && canDrop, cardsRejectedSpace: isOver && !canDrop })}
      >
        {isOver && canDrop && (
          <Box className={classes.overColumn}>
            <Typography variant="subtitle1" component="span">
              {title}
            </Typography>
          </Box>
        )}

        {isOver && !canDrop && (
          <Box className={classes.rejectedColumn}>
            <BlockOutlined color="error" fontSize="large" />
          </Box>
        )}

        <Box sx={{ visibility: isOver ? 'hidden' : 'visible', display: 'contents' }}>
          {cards.map((card, index) => (
            <BoardCard<T>
              key={id}
              id={id}
              card={card}
              index={index}
              columnId={id}
              metaData={metaData}
              onMoveCard={onMoveCard}
            >
              {renderCard(card, id)}
            </BoardCard>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

const useStyles = makeStyles(({ palette }) => ({
  overColumn: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectedColumn: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  boardColumn: {
    width: '100%',
    gap: '0.25rem',
    height: '100%',
    display: 'flex',
    minWidth: '20rem',
    flexDirection: 'column',

    '& .headerSpace': {
      borderRadius: '0.3rem',
      padding: '0.75rem 1rem',
      border: `1px solid ${grey[300]}`,
      backgroundColor: palette.background.paper,
    },

    '& .cardsSpace': {
      gap: '0.25rem',
      height: '100%',
      display: 'flex',
      overflow: 'auto',
      borderRadius: '0.3rem',
      flexDirection: 'column',
      padding: '0.5rem 0.75rem',
      border: `1px solid ${grey[300]}`,
      backgroundColor: palette.background.paper,
      position: 'relative',

      scrollbarWidth: 'none',
      msOverflowStyle: 'none',

      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },

    '& .cardsOverSpace': {
      backgroundColor: lightBlue[50],
    },

    '& .cardsRejectedSpace': {
      backgroundColor: red[50],
      borderColor: red[200],
    },
  },
}));
